/**
 * Consolidated API Client for Equilateral UI Monorepo
 * Following Tim-Combo pattern from HappyHippo
 * Reference: .equilateral-standards/frontend-development/tim_combo_frontend_auth_pattern.md
 *
 * Key Standards:
 * - Uses Make_Authorized_API_Call (Tim-Combo standard)
 * - Uses fetch (not axios)
 * - Bearer token authentication
 * - Standard response format with meta/data structure
 * - Dual API Gateway routing (core vs agents)
 * - Automatic token refresh with Cognito
 */

// API response types matching Tim-Combo standard
// Using TimCombo prefix to avoid conflicts with legacy types in types/index.ts
export interface TimComboAPISuccessResponse<T = any> {
  success: true;
  message: string;
  error_details?: string;
  error_code?: string;
  meta: {
    Total_Records: number;
    Request_ID: string;
    Timestamp: string;
    Company_ID?: string;
  };
  data: {
    Records: T[];
    Query_Context?: {
      Mode?: string;
      Operation?: string;
      Company_ID?: string;
    };
  };
}

export interface TimComboAPIErrorResponse {
  success: false;
  message: string;
  meta: {
    Timestamp: string;
    Request_ID?: string;
    Error_Source: string;
  };
  error_code: string;
  error_details: string;
  validation_errors?: {
    field: string;
    code: string;
    message: string;
  }[];
}

export type TimComboAPIResponse<T = any> = TimComboAPISuccessResponse<T> | TimComboAPIErrorResponse;

// Type guards
export function isSuccessResponse<T>(response: TimComboAPIResponse<T>): response is TimComboAPISuccessResponse<T> {
  return response.success === true;
}

export function isErrorResponse(response: TimComboAPIResponse): response is TimComboAPIErrorResponse {
  return response.success === false;
}

// Dual API Gateway Configuration (HappyHippo pattern)
// Gateway 1: Core operations (clients, teams, users, repositories, projects, etc.) - 126 handlers
const API_BASE_CORE = process.env.REACT_APP_API_URL_CORE || process.env.REACT_APP_API_URL || '';

// Gateway 2: Agent operations - 72 handlers
const API_BASE_AGENTS = process.env.REACT_APP_API_URL_AGENTS || API_BASE_CORE;

// Validate configuration
if (process.env.NODE_ENV === 'production') {
  if (!API_BASE_CORE) {
    console.error('[apiClient] REACT_APP_API_URL_CORE not configured');
  }
  if (API_BASE_CORE === API_BASE_AGENTS) {
    console.warn('[apiClient] REACT_APP_API_URL_AGENTS not configured - routing to core gateway');
  }
}

/**
 * Helper function to determine which API gateway to use based on endpoint path
 * Routes /api/agents/* to agents gateway, everything else to core gateway
 */
function getApiBaseForEndpoint(endpoint: string): string {
  if (endpoint.startsWith('/api/agents/')) {
    return API_BASE_AGENTS;
  }
  return API_BASE_CORE;
}

/**
 * Helper function to get authorization headers (Tim-Combo standard)
 * Uses 'token' key to match HappyHippo pattern, falls back to 'idToken' for ui-commercial
 */
export function getAuthHeaders(): { Authorization?: string } {
  // Try 'token' key first (ea-ui standard)
  let token = localStorage.getItem('token');

  // Fall back to 'idToken' (ui-commercial standard)
  if (!token) {
    token = localStorage.getItem('idToken');
  }

  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Clear authentication and redirect to login
 */
function clearAuthAndRedirect(): void {
  localStorage.removeItem('token');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('idToken');
  localStorage.removeItem('email_address');
  localStorage.removeItem('user');
  localStorage.removeItem('selectedClientId');
  localStorage.removeItem('selectedCompanyId');
  window.location.href = '/login';
}

/**
 * Internal function to check if token is expired or about to expire
 * Returns true if token is expired or will expire within 5 minutes
 */
function isTokenExpired(token: string, bufferSeconds: number = 300): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return true;
    }

    // Decode payload (base64url)
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

    // Check expiration with buffer (exp is in seconds, Date.now() is in milliseconds)
    if (payload.exp) {
      const expirationTime = payload.exp * 1000;
      const currentTime = Date.now();
      const bufferTime = bufferSeconds * 1000;

      return expirationTime - currentTime <= bufferTime;
    }

    return true;
  } catch (error) {
    console.error('[apiClient] Error decoding token:', error);
    return true;
  }
}

/**
 * Refresh authentication token using Cognito
 * This is a placeholder that should be replaced with actual Cognito refresh logic
 * when used in a context with Cognito user pool
 */
async function refreshAuthToken(): Promise<boolean> {
  // This will be implemented by the consuming application
  // For now, return false to trigger re-authentication
  console.warn('[apiClient] Token refresh not implemented - re-authentication required');
  return false;
}

/**
 * Make Authorized API Call (Tim-Combo Standard)
 * This is the canonical function name used across all Equilateral projects
 *
 * @param url - Full API URL or relative path
 * @param method - HTTP method
 * @param body - Request body for POST/PUT
 * @param options - Additional options (params, headers, skipTokenRefresh)
 */
export async function Make_Authorized_API_Call<T = any>(
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  body?: any,
  options?: {
    params?: Record<string, any>;
    headers?: Record<string, string>;
    skipTokenRefresh?: boolean;
  }
): Promise<TimComboAPIResponse<T>> {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    // Add authorization header (Tim-Combo standard: Bearer token)
    const authHeaders = getAuthHeaders();
    if (authHeaders.Authorization) {
      headers.Authorization = authHeaders.Authorization;
    } else {
      return {
        success: false,
        message: 'No authentication token found',
        error_code: 'UNAUTHORIZED',
        error_details: 'No authentication token found',
        meta: {
          Timestamp: new Date().toISOString(),
          Error_Source: 'Client'
        }
      } as TimComboAPIErrorResponse;
    }

    // Extract token for expiry check
    const token = authHeaders.Authorization.replace('Bearer ', '');

    // Check if token is expired or about to expire (skip for mock tokens and retry calls)
    const isMockToken = token.endsWith('.test');
    if (!options?.skipTokenRefresh && !isMockToken && isTokenExpired(token)) {
      console.log('[apiClient] Token expired or expiring soon, attempting refresh...');

      const refreshSuccess = await refreshAuthToken();

      if (refreshSuccess) {
        console.log('[apiClient] Token refreshed successfully, retrying API call...');
        // Retry the API call with the new token
        return Make_Authorized_API_Call<T>(url, method, body, { ...options, skipTokenRefresh: true });
      } else {
        // Refresh failed - user will be redirected to login
        clearAuthAndRedirect();
        throw new Error('Token refresh failed - please login again');
      }
    }

    // Merge custom headers
    if (options?.headers) {
      Object.assign(headers, options.headers);
    }

    // Auto-prepend API base URL for relative paths (starts with /)
    let finalUrl = url;
    if (url.startsWith('/') && !url.startsWith('//')) {
      finalUrl = getApiBaseForEndpoint(url) + url;
    }
    if (options?.params) {
      const queryParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      if (queryParams.toString()) {
        finalUrl += `?${queryParams.toString()}`;
      }
    }

    // Make request
    const response = await fetch(finalUrl, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined
    });

    const data = await response.json();

    // Handle 401 Unauthorized
    if (response.status === 401) {
      // Try to refresh token if we haven't already tried
      if (!options?.skipTokenRefresh && !isMockToken) {
        console.log('[apiClient] Received 401 Unauthorized, attempting token refresh...');

        const refreshSuccess = await refreshAuthToken();

        if (refreshSuccess) {
          console.log('[apiClient] Token refreshed after 401, retrying API call...');
          // Retry the API call with the new token
          return Make_Authorized_API_Call<T>(url, method, body, { ...options, skipTokenRefresh: true });
        }
      }

      // TEMPORARY: Don't clear auth for mock tokens (ending with ".test")
      // This allows temporary authentication to work while real API is down
      if (!isMockToken) {
        console.error('[apiClient] 401 Unauthorized - clearing auth and redirecting to login');
        clearAuthAndRedirect();
        throw new Error('Authentication failed - please login again');
      } else {
        console.warn('[apiClient] 401 with mock token - API unavailable but keeping auth intact');
      }
    }

    // Handle successful responses
    if (response.ok) {
      // Check if response already has correct structure
      if (typeof data === 'object' && 'success' in data) {
        return data as TimComboAPIResponse<T>;
      }
      // Wrap legacy responses in standard format
      return {
        success: true,
        message: 'Operation successful',
        meta: {
          Total_Records: Array.isArray(data) ? data.length : 1,
          Request_ID: crypto.randomUUID(),
          Timestamp: new Date().toISOString()
        },
        data: {
          Records: Array.isArray(data) ? data : [data]
        }
      } as TimComboAPISuccessResponse<T>;
    } else {
      // Handle error responses
      if (typeof data === 'object' && 'success' in data && !data.success) {
        return data as TimComboAPIErrorResponse;
      }
      // Wrap legacy error responses
      return {
        success: false,
        message: data.message || `API Error: ${response.status}`,
        error_code: data.error_code || response.status.toString(),
        error_details: data.error_details || response.statusText,
        meta: {
          Timestamp: new Date().toISOString(),
          Error_Source: 'API',
          Request_ID: data.Request_ID || 'unknown'
        }
      } as TimComboAPIErrorResponse;
    }
  } catch (error) {
    // Network or parsing errors
    return {
      success: false,
      message: 'Network error',
      error_code: 'NETWORK_ERROR',
      error_details: error instanceof Error ? error.message : 'Unknown error',
      meta: {
        Timestamp: new Date().toISOString(),
        Error_Source: 'Network'
      }
    } as TimComboAPIErrorResponse;
  }
}

// Export API base URLs for direct use if needed
export const API_GATEWAYS = {
  CORE: API_BASE_CORE,     // Core operations (126 handlers)
  AGENTS: API_BASE_AGENTS  // Agent operations (72 handlers)
};

// Legacy export for backward compatibility
export const API_BASE_URL = API_BASE_CORE;

// Re-export for compatibility (these use Make_Authorized_API_Call internally with smart routing)
export const apiClient = {
  get: <T>(endpoint: string, params?: Record<string, any>) =>
    Make_Authorized_API_Call<T>(endpoint, 'GET', undefined, { params }),

  post: <T>(endpoint: string, body?: any, params?: Record<string, any>) =>
    Make_Authorized_API_Call<T>(endpoint, 'POST', body, { params }),

  put: <T>(endpoint: string, body?: any, params?: Record<string, any>) =>
    Make_Authorized_API_Call<T>(endpoint, 'PUT', body, { params }),

  delete: <T>(endpoint: string, params?: Record<string, any>) =>
    Make_Authorized_API_Call<T>(endpoint, 'DELETE', undefined, { params })
};

// Log configuration on initialization
if (process.env.NODE_ENV === 'development') {
  console.log('[apiClient] Gateway Configuration:', {
    'Core Gateway': API_BASE_CORE || 'Not configured',
    'Agents Gateway': API_BASE_AGENTS || 'Not configured',
    'Total Handlers': '198 Lambda functions distributed across 2 gateways'
  });
  console.log('[apiClient] Following Tim-Combo standard with Make_Authorized_API_Call');
}
